import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Typo } from '@/components/ui/typography';
import { UserCartItem, useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { formatCurrency } from '@/lib/accounting';
import { formatDate } from '@/lib/date';
import { cn } from '@/lib/utils';
import { SessionData } from '@/types/session';
import { endOfDay, isPast, parse } from 'date-fns';
import { Calendar, ChevronsRight, Clock, DollarSign, Edit3, MapPin, Trash2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

interface CartServiceItemProps {
  item: UserCartItem;
  selected: boolean;
  onSelectionChange: (selected: boolean) => void;
  onRemove: () => void;
  onUpdateQuantity: (rowId: string, quantity: number) => void;
  onEdit: (item: UserCartItem) => void;
  disabled?: boolean;
  selectionDisabled?: boolean;
  showRemoveButton?: boolean;
  showEditButton?: boolean;
  className?: string;
}

export function CartServiceItem({
  item,
  selected,
  onSelectionChange,
  onRemove,
  onEdit,
  disabled = false,
  selectionDisabled = false,
  showRemoveButton = true,
  showEditButton = true,
  className = '',
}: CartServiceItemProps) {
  const [isRemoving, setIsRemoving] = useState(false);
  const { sessionsData, sessionsLoading, selectedSessions, setSelectedSessions, matchedRowIds, appliedVoucher } = useUserGlobalCart();

  const isVoucherMatched = matchedRowIds.includes(item.rowId);

  const handleRemove = async () => {
    try {
      setIsRemoving(true);
      await onRemove();
    } finally {
      setIsRemoving(false);
    }
  };

  const checkboxDisabled = useMemo(() => {
    if (!item || !item.options.has_inventory) return selectionDisabled || disabled || isRemoving;
    if (item.options.has_inventory && item.options.reservation_code) return selectionDisabled || disabled || isRemoving;
    return selectionDisabled || disabled || isRemoving || !selectedSessions[item.rowId];
  }, [selectionDisabled, disabled, item, isRemoving, selectedSessions]);

  const isExpired = useMemo(() => {
    if (!item) return false;
    if (!item.options.has_inventory || !item.options.reservation_code || !item.options.expires_at) return false;
    return isPast(parse(item.options.expires_at, 'dd/MM/yyyy hh:mm:ss a', new Date()));
  }, [item]);

  const isPastDate = useMemo(() => {
    if (!item.options.date) return false;
    return isPast(endOfDay(parse(item.options.date, 'dd/MM/yyyy', new Date())));
  }, [item.options.date]);

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        selected ? 'ring-primary ring-2 ring-offset-2' : 'hover:shadow-md',
        disabled && 'opacity-50',
        className,
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-baseline gap-4">
          {/* Checkbox */}
          <div className="flex items-center pt-1">
            <Tooltip>
              <TooltipTrigger>
                <Checkbox checked={selected} onCheckedChange={onSelectionChange} disabled={checkboxDisabled || isPastDate} className="size-6" />
              </TooltipTrigger>
              <TooltipContent side="right">
                {item.options.has_inventory && checkboxDisabled && 'Chọn suất để tiếp tục thanh toán'}
                {!checkboxDisabled && 'Đánh dấu để thêm vào đơn hàng thanh toán'}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-2">
            {/* Service Header */}
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-2">
                <Typo variant="h6" className="font-semibold text-gray-900">
                  {item.name}
                </Typo>
                {isVoucherMatched && appliedVoucher?.discount_label && (
                  <Badge variant="outline" className="border-green-200 bg-green-50 text-xs text-green-700">
                    -{appliedVoucher.discount_label} voucher
                  </Badge>
                )}
              </div>

              {/* {showEditButton && (
                <Button variant="ghost" onClick={() => onEdit(item)} disabled={disabled || isRemoving} className="hover:text-primary text-gray-400">
                  <Edit3 className="h-4 w-4" />
                  Sửa
                </Button>
              )} */}
            </div>

            {/* Location and Date */}
            <div className="flex flex-wrap items-baseline space-y-2 space-x-8">
              <Typo variant="small" className="text-muted-foreground flex items-center gap-1 font-medium">
                <Users className="inline-block size-4" />
                {item.options?.type_name} {item.qty && ` x ${item.qty}`}
              </Typo>
              <Typo variant="small" className="text-muted-foreground flex items-center gap-1 font-medium">
                <Calendar className="inline-block size-4" />
                {item.options.date}
              </Typo>
              <Typo variant="small" className="text-muted-foreground flex items-center gap-1 font-medium">
                <DollarSign className="inline-block size-4" />
                {formatCurrency(item.price)}
              </Typo>
            </div>

            {/* Session Selector */}
            <div className="flex items-center justify-between gap-3">
              {item.options.has_inventory && !item.options.reservation_code && (
                <>
                  {sessionsLoading && (
                    <div className="mt-2 flex flex-col space-y-2">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-8 w-[200px]" />
                    </div>
                  )}
                  {!sessionsLoading && sessionsData[item.rowId] && (
                    <div className="mt-2 flex flex-col space-y-2">
                      <Label htmlFor="session-select" className="text-xs font-normal">
                        Chọn suất (giờ checkin):
                      </Label>
                      <Select
                        value={selectedSessions[item.rowId] ? selectedSessions[item.rowId].session_id.toString() : ''}
                        onValueChange={(value) =>
                          setSelectedSessions((prev: Record<string, SessionData>) => ({
                            ...prev,
                            [item.rowId]: sessionsData[item.rowId]?.find((session) => session.session_id === parseInt(value)) as SessionData,
                          }))
                        }
                        disabled={!sessionsData[item.rowId]}
                      >
                        <SelectTrigger
                          id="session-select"
                          className="cursor-pointer [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap"
                        >
                          <SelectValue placeholder="Chọn suất(giờ checkin)" />
                        </SelectTrigger>
                        <SelectContent>
                          {sessionsData[item.rowId]?.map((session) => (
                            <SelectItem key={session.session_id} disabled={item.qty > session.inventory} value={session.session_id.toString()}>
                              {`${session.outlet_name}, ${session.start_time} (còn lại ${session.inventory} chỗ)`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {!sessionsLoading && !sessionsData[item.rowId] && (
                    <Typo variant="small" className="text-muted-foreground flex items-center gap-1 font-medium">
                      <Calendar className="inline-block size-4" />
                      Không có khung giờ khả dụng
                    </Typo>
                  )}
                </>
              )}

              {item.options.has_inventory && item.options.reservation_code && (
                <div className="mt-2 flex flex-col space-y-2">
                  <Typo variant="small" className="text-muted-foreground flex items-center gap-1 font-medium">
                    <Clock className="inline-block size-4" />
                    {item.options.outlet_name}, {item.options.start_time}
                  </Typo>
                </div>
              )}
            </div>
            {/* Quantity and Price */}
            <div className="flex items-center justify-between gap-3">
              <Typo variant="small" className="text-muted-foreground">
                thành tiền: <span className="text-primary text-lg font-semibold">{formatCurrency(item.total)}</span>
              </Typo>
            </div>
            {/* Quantity and Price */}
            <div className="flex items-center justify-between gap-3 border-t border-dashed pt-2">
              <div className="flex-1">
                <Button variant="ghost" onClick={() => onEdit(item)} disabled={disabled || isRemoving} className="hover:text-primary text-blue-500">
                  Xem chi tiết
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex space-x-3">
                {showEditButton && !isExpired && (
                  <Button variant="ghost" onClick={() => onEdit(item)} disabled={disabled || isRemoving} className="hover:text-primary text-gray-400">
                    <Edit3 className="h-4 w-4" />
                    Sửa
                  </Button>
                )}
                {showRemoveButton && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleRemove}
                    disabled={disabled || isRemoving}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Group Items Component for organizing cart items by site and date
interface ServiceGroupProps {
  siteName: string;
  date: string;
  items: UserCartItem[];
  selectedItems: Set<string>;
  onItemSelectionChange: (itemId: string, selected: boolean) => void;
  onItemRemove: (itemId: string) => void;
  onUpdateQuantity: (rowId: string, quantity: number) => void;
  onItemEdit: (item: UserCartItem) => void;
  disabled?: boolean;
  className?: string;
}

export function ServiceGroup({
  siteName,
  date,
  items,
  selectedItems,
  onItemSelectionChange,
  onItemRemove,
  onUpdateQuantity,
  onItemEdit,
  disabled = false,
  className = '',
}: ServiceGroupProps) {
  const formatCartItemDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return formatDate(dateString);
    } catch {
      return dateString;
    }
  };

  const allItemsSelected = items.every((item) => selectedItems.has(item.id));
  const someItemsSelected = items.some((item) => selectedItems.has(item.id));

  const handleGroupSelectionChange = (selected: boolean) => {
    items.forEach((item) => {
      onItemSelectionChange(item.id, selected);
    });
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Group Header */}
      <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={allItemsSelected}
            ref={(ref) => {
              if (ref) {
                (ref as any).indeterminate = someItemsSelected && !allItemsSelected;
              }
            }}
            onCheckedChange={handleGroupSelectionChange}
            disabled={disabled}
            className="h-5 w-5"
          />
          <div>
            <div className="flex items-center gap-2">
              <MapPin className="text-primary h-4 w-4" />
              <Typo variant="h6" className="font-semibold text-gray-900">
                {siteName}
              </Typo>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <Typo variant="small" className="text-gray-600">
                {formatCartItemDate(date)}
              </Typo>
            </div>
          </div>
        </div>
        <Typo variant="small" className="text-gray-600">
          {items.length} dịch vụ
        </Typo>
      </div>

      {/* Group Items */}
      <div className="space-y-3 pl-6">
        {items.map((item) => (
          <CartServiceItem
            key={item.id}
            item={item}
            selected={selectedItems.has(item.id)}
            onSelectionChange={(selected) => onItemSelectionChange(item.id, selected)}
            onRemove={() => onItemRemove(item.id)}
            onUpdateQuantity={(rowId, quantity) => onUpdateQuantity(rowId, quantity)}
            onEdit={(item) => onItemEdit(item)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}
